// class WeightReaderManager {
//   constructor() {
//     this.isConnected = false;
//     this.weight = 0;
//     this.listeners = new Set();
//     this.portRef = null;
//     this.readerRef = null;
//     this.keepReadingRef = false;
//     this.BAUD_RATES = [9600, 4800, 19200];
//   }

//   subscribe(listener) {
//     this.listeners.add(listener);
//     listener(this.weight);
//     return () => this.listeners.delete(listener);
//   }

//   notify(weight) {
//     this.weight = weight;
//     for (const fn of this.listeners) fn(weight);
//   }

//   async tryReconnect() {
//     try {
//       const ports = await navigator.serial.getPorts();
//       if (ports.length > 0) {
//         console.log("🔁 Reconnecting...");
//         this.portRef = ports[0];
//         await this.connectToSerial(ports[0]);
//       }
//     } catch (err) {
//       console.error("Reconnect error:", err);
//     }
//   }

//   async connectToSerial(existingPort = null) {
//     try {
//       const port = existingPort || (await navigator.serial.requestPort());
//       this.portRef = port;
//       await port.open({ baudRate: 9600 });
//       this.isConnected = true;
//       this.keepReadingRef = true;
//       localStorage.setItem("rs232Connected", "true");

//       const textDecoder = new TextDecoderStream();
//       port.readable.pipeTo(textDecoder.writable);
//       const reader = textDecoder.readable.getReader();
//       this.readerRef = reader;
//       this.readLoop(reader);
//     } catch (err) {
//       console.error("connectToSerial error:", err);
//       this.isConnected = false;
//       this.notify(0);
//       throw err;
//     }
//   }

//   async readLoop(reader) {
//     let buffer = "";
//     try {
//       while (this.keepReadingRef) {
//         const { value, done } = await reader.read();
//         if (done) break;
//         if (value) {
//           buffer += value;
//           const lines = buffer.split(/[\r\n]+/);
//           buffer = lines.pop();
//           for (const line of lines) {
//             const match = line.match(/[-+]?\d*\.?\d+/);
//             if (match) {
//               let parsed = parseFloat(match[0]);
//               if (parsed > 10) parsed = parsed / 1000; // ✅ grams → kg
//               const formatted = parseFloat(parsed.toFixed(3));
//               this.notify(formatted);
//             }
//           }
//         }
//       }
//     } catch (err) {
//       console.error("⚠️ Read error:", err);
//     } finally {
//       try {
//         reader.releaseLock();
//       } catch {}
//     }
//   }

//   async disconnect() {
//     try {
//       this.keepReadingRef = false;
//       if (this.readerRef) {
//         await this.readerRef.cancel().catch(() => {});
//         this.readerRef.releaseLock();
//         this.readerRef = null;
//       }
//       if (this.portRef) {
//         await this.portRef.close().catch(() => {});
//         this.portRef = null;
//       }
//       this.isConnected = false;
//       localStorage.removeItem("rs232Connected");
//       this.notify(0);
//     } catch (err) {
//       console.error("disconnect error:", err);
//     }
//   }
// }

// const instance = new WeightReaderManager();
// export default instance;
class WeightReaderManager {
  constructor() {
    this.isConnected = false;
    this.weight = 0;
    this.listeners = new Set();
    this.portRef = null;
    this.readerRef = null;
    this.keepReadingRef = false;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.weight);
    return () => this.listeners.delete(listener);
  }

  notify(weight) {
    this.weight = weight;
    for (const fn of this.listeners) fn(weight);
  }

  async tryReconnect() {
    try {
      const ports = await navigator.serial.getPorts();
      if (ports.length > 0) {
        console.log("🔁 Attempting auto-reconnect...");
        await this.connectToSerial(ports[0]);
      }
    } catch (err) {
      console.error("Reconnect error:", err);
    }
  }

  async connectToSerial(existingPort = null) {
    try {
      // ✅ Prevent re-opening if already connected
      if (this.isConnected) {
        console.log("⚠️ Port already connected, skipping open");
        return;
      }

      const port = existingPort || (await navigator.serial.requestPort());
      this.portRef = port;

      // ✅ Skip open if it's already open (browser bug prevention)
      if (port.readable && port.writable) {
        console.log("⚠️ Port is already open, reusing existing session");
      } else {
        await port.open({ baudRate: 9600 });
      }

      this.isConnected = true;
      this.keepReadingRef = true;
      localStorage.setItem("rs232Connected", "true");

      const textDecoder = new TextDecoderStream();
      port.readable.pipeTo(textDecoder.writable).catch(() => {});
      const reader = textDecoder.readable.getReader();
      this.readerRef = reader;

      this.readLoop(reader).catch((err) =>
        console.warn("Read loop ended:", err?.message || err)
      );
    } catch (err) {
      if (err.name === "InvalidStateError") {
        console.warn("⚠️ Port already open, marking as connected");
        this.isConnected = true;
      } else {
        console.error("connectToSerial error:", err);
        this.isConnected = false;
        this.notify(0);
        throw err;
      }
    }
  }

  async readLoop(reader) {
    let buffer = "";
    try {
      while (this.keepReadingRef) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) {
          buffer += value;
          const lines = buffer.split(/[\r\n]+/);
          buffer = lines.pop();
          for (const line of lines) {
            const match = line.match(/[-+]?\d*\.?\d+/);
            if (match) {
              let parsed = parseFloat(match[0]);
              if (parsed > 10) parsed = parsed / 1000;
              const formatted = parseFloat(parsed.toFixed(3));
              this.notify(formatted);
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.warn("⚠️ Read error:", err);
      }
    } finally {
      try {
        reader.releaseLock();
      } catch {}
    }
  }

  async disconnect() {
    try {
      this.keepReadingRef = false;

      if (this.readerRef) {
        const reader = this.readerRef;
        this.readerRef = null;
        try {
          await reader.cancel();
        } catch {}
        try {
          reader.releaseLock();
        } catch {}
      }

      await new Promise((res) => setTimeout(res, 200));

      if (this.portRef) {
        try {
          await this.portRef.close();
        } catch (err) {
          console.warn("⚠️ Port close warning:", err);
        }
        this.portRef = null;
      }

      this.isConnected = false;
      localStorage.removeItem("rs232Connected");
      this.notify(0);
      console.log("🔌 Disconnected cleanly");
    } catch (err) {
      console.error("disconnect error:", err);
    }
  }
}

const instance = new WeightReaderManager();
export default instance;
